import React from 'react';
import { CatmullRomCurve3, Matrix4, Quaternion, Vector3 } from 'three';

import { formatMoment } from '@paragon/client-tools';
import { RenderOrder } from '@paragon/three-shared';
import { leg2D } from '@paragon/utilities';

import { LabelVisual } from '../util/LabelVisual';

const radius = 5;
const numberOfPoints = 5;
const interval = radius / numberOfPoints;

let x = interval * 2;
const z = 0;

const points = [];

for (let i = numberOfPoints - 2; i >= 0; i -= 1) {
  const y = leg2D(radius, x);
  points.push(new Vector3(x, y, z));
  x += interval;
}
x -= interval * 2;

for (let i = numberOfPoints - 3; i >= 0; i -= 1) {
  const y = leg2D(radius, x) * -1;
  points.push(new Vector3(x, y, z));
  x -= interval;
}

const path = new CatmullRomCurve3(points);
const lookAtPoint = new Vector3(0, radius / 3, 0);
const headPosition = points[0];
const lookRotationMatrix = new Matrix4().lookAt(lookAtPoint, headPosition, new Vector3(0, 1, 0));
const headQuaternion = new Quaternion().setFromRotationMatrix(lookRotationMatrix);
const middlePosition = {
  x: x + interval * 2,
  y: leg2D(radius, x - interval) + 2,
  z: 0,
};

interface Props {
  readonly moment: number;
}

export function MomentVisual({ moment }: Props) {
  if (Math.abs(moment) < 0.5) return null;

  const xScale = moment >= 0 ? 1 : -1;

  return Math.abs(moment) >= 0.5 ? (
    <object3D>
      <mesh scale={[xScale, 1, 1]} renderOrder={RenderOrder.MomentVisual}>
        <tubeGeometry args={[path, 10, 0.3, 10, false]} />
        <meshBasicMaterial color={moment >= 0 ? 0xf0ff0f : 0x0ff0ff} depthTest={false} transparent />
        <mesh position={headPosition} quaternion={headQuaternion} renderOrder={RenderOrder.MomentVisual}>
          <cylinderGeometry args={[0.005, 0.6, 2, 10, 10]} />
          <meshBasicMaterial color={moment >= 0 ? 0xf0ff0f : 0x0ff0ff} depthTest={false} transparent />
        </mesh>
      </mesh>
      <LabelVisual fontSize={1} position={[middlePosition.x * xScale, middlePosition.y, middlePosition.z]}>
        {formatMoment(moment)}
      </LabelVisual>
    </object3D>
  ) : null;
}
